import { DEFAULT_TIMEZONE, type Languages, LOCALES } from '@avanta/shared/constants/common';
import { getLanguage } from '@avanta/shared/utils/date';
import { subDays } from 'date-fns';
import { fromZonedTime, toZonedTime, format as formatTz } from 'date-fns-tz';
import { useUserStore } from 'stores/user';

export { isMoreThanSecondsBefore } from '@avanta/shared/utils/date';

const getUserTz = (): string => useUserStore.getState().user?.timeZone ?? DEFAULT_TIMEZONE;

const getCalendarDayInTz = (date: Date, timezone: string): string =>
  formatTz(toZonedTime(date, timezone), 'yyyy-MM-dd', { timeZone: timezone });

export const isTodayForUser = (dateTime: string): boolean => {
  const timezone = getUserTz();

  return (
    formatUnifiedForDisplay(dateTime, 'yyyy-MM-dd', timezone) ===
    getCalendarDayInTz(new Date(), timezone)
  );
};

export const isYesterdayForUser = (dateTime: string): boolean => {
  const timezone = getUserTz();

  return (
    formatUnifiedForDisplay(dateTime, 'yyyy-MM-dd', timezone) ===
    getCalendarDayInTz(subDays(new Date(), 1), timezone)
  );
};

export const generateMonthOptions = (locale = 'es-ES'): { value: string; label: string }[] => {
  const formatter = new Intl.DateTimeFormat(locale, { month: 'long' });
  return Array.from({ length: 12 }, (_, i) => ({
    label: formatter.format(new Date(2000, i)),
    value: String(i + 1),
  }));
};

export const generateYearOptions = (): { value: string; label: string }[] => {
  const currentYear = new Date().getFullYear();
  return Array.from({ length: 100 }, (_, i) => ({
    label: String(currentYear - i),
    value: String(currentYear - i),
  }));
};

export function isAdult(value: string): boolean | string {
  const birthYear = Number(value);
  if (isNaN(birthYear) || birthYear < 1900 || birthYear > new Date().getFullYear()) {
    return false;
  }

  const currentYear = new Date().getFullYear();
  const age = currentYear - birthYear;

  if (age < 18) {
    return 'Debes tener al menos 18 años';
  }

  return true;
}

export function formatUnifiedForDisplay(
  unifiedDateTime: string,
  displayFormat = 'dd/MM/yyyy HH:mm',
  timezone?: string
): string {
  return formatForUserTz(unifiedDateTime, timezone ?? getUserTz(), displayFormat);
}

function formatForUserTz(dateTimeStr: string, userTz: string, displayFormat: string): string {
  const utcDate = fromZonedTime(dateTimeStr, DEFAULT_TIMEZONE);
  const userDate = toZonedTime(utcDate, userTz);
  return formatTz(userDate, displayFormat, {
    timeZone: userTz,
    locale: LOCALES[getLanguage() as Languages],
  });
}
